export const BACKEND_PUBLIC_V1_PREFIX = "/be/v1";
export const BACKEND_INTERNAL_V1_PREFIX = "/be/internal/v1";
export const SESSIONMGR_PUBLIC_V1_PREFIX = "/sess/v1";
export const SESSIONMGR_INTERNAL_V1_PREFIX = "/sess/internal/v1";
const SESSIONMGR_LEGACY_PUBLIC_PREFIX_MAPPINGS: ReadonlyArray<readonly [string, string]> = [
  ["/chat/", `${SESSIONMGR_PUBLIC_V1_PREFIX}/chat/`],
  ["/llmproxy/", `${SESSIONMGR_PUBLIC_V1_PREFIX}/llmproxy/`],
  ["/realtime/", `${SESSIONMGR_PUBLIC_V1_PREFIX}/realtime/`],
  ["/desktop/", `${SESSIONMGR_PUBLIC_V1_PREFIX}/desktop/`],
  ["/turn/", `${SESSIONMGR_PUBLIC_V1_PREFIX}/turn/`],
  ["/ws/remote-agent/", `${SESSIONMGR_PUBLIC_V1_PREFIX}/ws/remote-agents/`],
  ["/ws/", `${SESSIONMGR_PUBLIC_V1_PREFIX}/ws/`],
  ["/widget/", `${SESSIONMGR_PUBLIC_V1_PREFIX}/widget/`]
];

export const joinServicePath = (prefix: string, path: string): string => {
  const normalizedPath = path.startsWith("/") ? path : `/${path}`;
  return `${prefix}${normalizedPath}`;
};

const isAbsoluteUrl = (value: string): boolean => /^[a-zA-Z][a-zA-Z0-9+.-]*:/.test(value);

const normalizeServiceBaseUrl = (baseUrl: string | undefined | null): string | undefined => {
  const normalizedBaseUrl = baseUrl?.trim().replace(/\/+$/, "");
  return normalizedBaseUrl ? normalizedBaseUrl : undefined;
};

const normalizeAbsoluteServiceBaseUrl = (baseUrl: string | undefined | null): string | undefined => {
  const normalizedBaseUrl = normalizeServiceBaseUrl(baseUrl);
  if (!normalizedBaseUrl || normalizedBaseUrl.startsWith("/")) {
    return undefined;
  }

  return isAbsoluteUrl(normalizedBaseUrl) ? normalizedBaseUrl : `https://${normalizedBaseUrl.replace(/^\/\//, "")}`;
};

const parseServiceBaseUrl = (baseUrl: string | undefined | null): URL | null => {
  const normalizedBaseUrl = normalizeAbsoluteServiceBaseUrl(baseUrl);
  if (!normalizedBaseUrl) {
    return null;
  }

  try {
    return new URL(normalizedBaseUrl);
  } catch {
    return null;
  }
};

export const joinServiceUrl = (baseUrl: string | undefined | null, path: string): string => {
  if (isAbsoluteUrl(path)) {
    return path;
  }

  const normalizedPath = path.startsWith("/") ? path.slice(1) : path;
  const parsedBaseUrl = parseServiceBaseUrl(baseUrl);

  if (!parsedBaseUrl) {
    return `/${normalizedPath}`;
  }

  const basePath = parsedBaseUrl.pathname.replace(/\/+$/, "");
  if (basePath && basePath !== "/" && (path === basePath || path.startsWith(`${basePath}/`))) {
    const strippedPath = path.slice(basePath.length);
    if (strippedPath === "") {
      return `${parsedBaseUrl.origin}${basePath}`;
    }

    return new URL(
      strippedPath.startsWith("/") ? strippedPath.slice(1) : strippedPath,
      `${parsedBaseUrl.origin}${basePath}/`
    ).toString();
  }

  return new URL(normalizedPath, `${parsedBaseUrl.origin}${basePath === "/" ? "/" : `${basePath}/`}`).toString();
};

export const getServiceUrlOrigin = (baseUrl: string | undefined | null): string | undefined => {
  return parseServiceBaseUrl(baseUrl)?.origin;
};

export const getServiceUrlHost = (baseUrl: string | undefined | null): string | undefined => {
  return parseServiceBaseUrl(baseUrl)?.host;
};

const hasBackendV1Prefix = (path: string): boolean => {
  return (
    path === BACKEND_PUBLIC_V1_PREFIX ||
    path.startsWith(`${BACKEND_PUBLIC_V1_PREFIX}/`) ||
    path === BACKEND_INTERNAL_V1_PREFIX ||
    path.startsWith(`${BACKEND_INTERNAL_V1_PREFIX}/`)
  );
};

export const backendPublicPath = (path: string): string => {
  const normalizedPath = path.startsWith("/") ? path : `/${path}`;
  return hasBackendV1Prefix(normalizedPath)
    ? normalizedPath
    : joinServicePath(BACKEND_PUBLIC_V1_PREFIX, normalizedPath);
};

export const backendInternalPath = (path: string): string => {
  const normalizedPath = path.startsWith("/") ? path : `/${path}`;
  return hasBackendV1Prefix(normalizedPath)
    ? normalizedPath
    : joinServicePath(BACKEND_INTERNAL_V1_PREFIX, normalizedPath);
};

const hasSessionmgrV1Prefix = (path: string): boolean => {
  return (
    path === SESSIONMGR_PUBLIC_V1_PREFIX ||
    path.startsWith(`${SESSIONMGR_PUBLIC_V1_PREFIX}/`) ||
    path === SESSIONMGR_INTERNAL_V1_PREFIX ||
    path.startsWith(`${SESSIONMGR_INTERNAL_V1_PREFIX}/`)
  );
};

export const sessionmgrPublicPath = (path: string): string => {
  const normalizedPath = path.startsWith("/") ? path : `/${path}`;
  return hasSessionmgrV1Prefix(normalizedPath)
    ? normalizedPath
    : joinServicePath(SESSIONMGR_PUBLIC_V1_PREFIX, normalizedPath);
};

export const sessionmgrInternalPath = (path: string): string => {
  const normalizedPath = path.startsWith("/") ? path : `/${path}`;
  return hasSessionmgrV1Prefix(normalizedPath)
    ? normalizedPath
    : joinServicePath(SESSIONMGR_INTERNAL_V1_PREFIX, normalizedPath);
};

const normalizePathPrefix = (value: string): string => {
  if (!value) {
    return "/";
  }
  return value.startsWith("/") ? value : `/${value}`;
};

export const canonicalizeSessionmgrPublicPath = (path: string): string => {
  const normalizedPath = normalizePathPrefix(path);
  for (const [legacyPrefix, canonicalPrefix] of SESSIONMGR_LEGACY_PUBLIC_PREFIX_MAPPINGS) {
    const legacyPrefixNoSlash = legacyPrefix.endsWith("/") ? legacyPrefix.slice(0, -1) : legacyPrefix;
    const canonicalPrefixNoSlash = canonicalPrefix.endsWith("/") ? canonicalPrefix.slice(0, -1) : canonicalPrefix;
    if (normalizedPath === legacyPrefixNoSlash) {
      return canonicalPrefixNoSlash;
    }
    if (normalizedPath.startsWith(legacyPrefix)) {
      return `${canonicalPrefix}${normalizedPath.slice(legacyPrefix.length)}`;
    }
  }
  return normalizedPath;
};
